import React, { useState } from 'react';
import styles from './TermsOfUse.module.css';

const TERMS = [
  {
    num: '01',
    title: 'Admission Confirmation',
    content: (
      <p>Admission is valid only after fee payment (full/partial) and document verification.</p>
    ),
  },
  {
    num: '02',
    title: 'Attendance Policy',
    tag: 'MANDATORY',
    content: (
      <>
        <p>Minimum <strong>75% attendance</strong> is mandatory. Attendance is recorded daily.</p>
        <p className={styles.violationLabel}>Violation Actions:</p>
        <ul>
          <li><strong>Below 90%</strong> → Not allowed for exams/interviews</li>
          <li><strong>Below 80%</strong> → Suspension or detention</li>
          <li><strong>Continuous absence</strong> → Admission cancellation</li>
        </ul>
      </>
    ),
  },
  {
    num: '03',
    title: 'Uniform & Grooming',
    content: (
      <>
        <p>Aviation grooming standards must be followed. Uniform is <strong>mandatory</strong>.</p>
        <p className={styles.violationLabel}>Violation Actions:</p>
        <ul>
          <li><strong>First time</strong> → Warning / fine / removal</li>
          <li><strong>Repeated</strong> → Suspension or termination</li>
        </ul>
      </>
    ),
  },
  {
    num: '04',
    title: 'Discipline & Conduct',
    content: (
      <p>Professional behavior is required at all times. Misconduct, abuse, harassment, or fighting is <strong>strictly prohibited</strong>.</p>
    ),
  },
  {
    num: '05',
    title: 'Mobile Phone Policy',
    content: (
      <p>Mobile phones are not allowed during lectures unless explicitly permitted by the instructor.</p>
    ),
  },
  {
    num: '06',
    title: 'Academic Requirements',
    content: (
      <ul>
        <li>Assignments and exams must be completed on time</li>
        <li>Mock interviews and training are compulsory</li>
      </ul>
    ),
  },
  {
    num: '07',
    title: 'Academy Property',
    content: (
      <p>Any damage to academy property will be chargeable to the student.</p>
    ),
  },
  {
    num: '08',
    title: 'Fee Payment Policy',
    content: (
      <ul>
        <li>Fees must be paid on time</li>
        <li>Late payment will result in penalties</li>
      </ul>
    ),
  },
  {
    num: '09',
    title: 'Certification Policy',
    content: (
      <>
        <p>Certificates will be issued only if:</p>
        <ul>
          <li>Course completed successfully</li>
          <li>Attendance criteria fulfilled</li>
          <li>All dues cleared</li>
        </ul>
      </>
    ),
  },
  {
    num: '10',
    title: 'False Information',
    content: (
      <p>Fake or incorrect information is <strong>strictly prohibited</strong> and will lead to immediate termination of admission.</p>
    ),
  },
  {
    num: '11',
    title: 'Personal Belongings',
    content: (
      <p>The academy is not responsible for any loss or damage to personal belongings on campus.</p>
    ),
  },
  {
    num: '12',
    title: 'No Job Guarantee',
    content: (
      <>
        <p>Only training and placement assistance is provided.</p>
        <p>There is <strong>no guarantee</strong> for job, interview call, or salary package.</p>
      </>
    ),
  },
  {
    num: '13',
    title: 'No Refund Policy',
    tag: 'IMPORTANT',
    highlight: true,
    content: (
      <>
        <p>Fees are <strong>NON-REFUNDABLE</strong> under any circumstances, including:</p>
        <ul>
          <li>Course discontinuation</li>
          <li>Absence</li>
          <li>Medical or personal reasons</li>
          <li>Disciplinary termination</li>
          <li>Failure or dissatisfaction</li>
        </ul>
        <div className={styles.importantNote}>
          ⚠️ <strong>Important:</strong> No refund even if admission is cancelled due to rule violation.
        </div>
      </>
    ),
  },
  {
    num: '14',
    title: 'Policy Updates',
    content: (
      <ul>
        <li>Students must follow updated rules at all times</li>
        <li>Ignorance of updated rules is not accepted as an excuse</li>
      </ul>
    ),
  },
  {
    num: '15',
    title: 'Authority of Management',
    content: (
      <>
        <p>Management reserves the right to modify:</p>
        <ul>
          <li>Courses and curriculum</li>
          <li>Timings and schedules</li>
          <li>Fees and payment structure</li>
          <li>Policies and rules</li>
        </ul>
      </>
    ),
  },
  {
    num: '16',
    title: 'Legal Jurisdiction',
    content: (
      <p>All disputes shall be subject to courts in <strong>Pune, Maharashtra</strong> only.</p>
    ),
  },
  {
    num: '17',
    title: 'Final Note',
    content: (
      <p>All rules and policies are defined by <strong>VIN Fly Aviation Academy</strong> under <strong>VPB Education Foundation</strong>. Enrollment implies full acceptance of all stated terms.</p>
    ),
  },
];

const TermsOfUse = () => {
  const [expanded, setExpanded] = useState(null);
  const toggle = (num) => setExpanded(prev => prev === num ? null : num);

  return (
    <main>
      {/* Hero */}
      <section className="page-hero" data-theme="dark">
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(135deg, #0a1628 0%, #1a2a4a 60%, #0d1f3c 100%)', zIndex: 0 }} />
        <div className={styles.heroGrid} />
        <div className="container page-hero__content" style={{ position: 'relative', zIndex: 1 }}>
          <p className="page-hero__breadcrumb">HOME / TERMS OF USE</p>
          <h1 className="page-hero__title">
            <span className="page-hero__title-top" style={{ color: '#FFFFFF', opacity: 1 }}>TERMS OF</span>
            <span className="page-hero__title-bottom" style={{ color: 'var(--cyan)' }}>USE.</span>
          </h1>
          <p className="page-hero__sub">Rules and policies governing your enrollment at VIN Fly Aviation Academy.</p>
        </div>
      </section>

      {/* Content */}
      <section className={styles.termsSection}>
        <div className="container" style={{ maxWidth: 860 }}>
          <div className={styles.metaBadge}>
            <span className={styles.metaIcon}>📋</span>
            <div>
              <p className={styles.metaTitle}>VIN Fly Aviation Academy</p>
              <p className={styles.metaSub}>Under VPB Education Foundation &nbsp;·&nbsp; Effective: 2026</p>
            </div>
          </div>

          <div className={styles.accordion}>
            {TERMS.map((t) => {
              const open = expanded === t.num;
              return (
                <div
                  key={t.num}
                  className={`${styles.item} ${t.highlight ? styles.itemHighlight : ''} ${open ? styles.itemOpen : ''}`}
                >
                  <button
                    className={styles.itemHeader}
                    onClick={() => toggle(t.num)}
                    aria-expanded={open}
                    id={`terms-item-${t.num}`}
                  >
                    <div className={styles.itemLeft}>
                      <span className={styles.itemNum}>{t.num}</span>
                      <span className={styles.itemTitle}>{t.title}</span>
                      {t.tag && (
                        <span className={`${styles.itemTag} ${t.highlight ? styles.tagRed : styles.tagBlue}`}>
                          {t.tag}
                        </span>
                      )}
                    </div>
                    <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </button>

                  <div className={`${styles.itemBody} ${open ? styles.itemBodyOpen : ''}`}>
                    <div className={styles.itemContent}>{t.content}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className={styles.footNote}>
            By enrolling at VIN Fly Aviation Academy, you agree to all terms listed above. For queries, contact{' '}
            <a href="mailto:contact@vinaviation.com" className={styles.footLink}>contact@vinaviation.com</a>
          </p>
        </div>
      </section>
    </main>
  );
};

export default TermsOfUse;
